import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { map, mergeMap, retryWhen } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export class AppHttpClientConfig {
  backendUrl = environment.backend.url;
  maxRetries: number = 3;
  backoffMs: number = 300;
}

@Injectable({
  providedIn: 'root',
})
export class AppHttpClientService {
  constructor(private httpClient: HttpClient) {}

  get<T>(url: string, opts: AppHttpClientConfig = new AppHttpClientConfig()) {
    return this.httpClient
      .get(`${opts.backendUrl}/${url}`, { observe: 'response' })
      .pipe(
        this.retryWithBackoff(opts.maxRetries, opts.backoffMs),
        map((res: HttpResponse<Object>) => this.handleResponse(res) as T)
      );
  }

  getOrNoContent<T>(url: string, opts: AppHttpClientConfig = new AppHttpClientConfig()): Observable<T | null> {
    return this.httpClient
      .get(`${opts.backendUrl}/${url}`, { observe: 'response' })
      .pipe(
        this.retryWithBackoff(opts.maxRetries, opts.backoffMs),
        mergeMap((res: HttpResponse<Object>) => {
          if (res.status === 200) {
            return of((res.body as { data: T }).data);
          }
          if (res.status === 204) {
            return of(null);
          }
          return throwError(res.body);
        })
      );
  }

  post(url: string, body: any, opts: AppHttpClientConfig = new AppHttpClientConfig()) {
    return this.httpClient
      .post(`${opts.backendUrl}/${url}`, body, { observe: 'response' })
      .pipe(
        this.retryWithBackoff(opts.maxRetries, opts.backoffMs),
        map((res: HttpResponse<Object>) => this.handleResponse(res))
      ).toPromise();
  }

  put(url: string, body: any, opts: AppHttpClientConfig = new AppHttpClientConfig()) {
    const request$ = this.httpClient
      .put(`${opts.backendUrl}/${url}`, body, { observe: 'response' })
      .pipe(
        this.retryWithBackoff(opts.maxRetries, opts.backoffMs),
        map((res: HttpResponse<Object>) => this.handleResponse(res))
      );
    
    return request$.toPromise();
  }

  delete(url: string, id: any = null, extraArgs = '', opts: AppHttpClientConfig = new AppHttpClientConfig()) {
    const finalUrl = `${url}${id ? '/' + id : ''}${extraArgs ? '?' + extraArgs : ''}`;
    return this.httpClient
      .delete(`${opts.backendUrl}/${finalUrl}`, { observe: 'response' })
      .pipe(
        this.retryWithBackoff(opts.maxRetries, opts.backoffMs),
        map((res: HttpResponse<Object>) => this.handleResponse(res))
      )
      .toPromise();
  }

  private handleResponse(res: HttpResponse<Object>): any {
    switch (res.status) {
      case 200:
        return (res.body as any).data;
      case 204:
        return true;
      default:
        return throwError(res.body);
    }
  }

  private retryWithBackoff(maxRetries: number, backoffMs: number) {
    return retryWhen((errors: Observable<any>) => {
      if (maxRetries <= 0) {
        return errors.pipe(mergeMap((error: HttpErrorResponse) => throwError(error)));
      }
      
      return errors.pipe(
        mergeMap((error: HttpErrorResponse, index: number) => {
          const retryAttempt = index + 1;
          const status = error?.status;

          if (retryAttempt > maxRetries) {
            return throwError(error);
          }

          const isRetryable =
            status === undefined ||
            status === 0 ||
            (status >= 500 && status < 600);
          if (!isRetryable) {
            return throwError(error);
          }

          const delay = backoffMs * retryAttempt;
          return timer(delay);
        })
      );
    });
  }
}
